// @todo: Migrate to @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components/Button';
// @todo: Migrate to 'Icon' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Icon, IconName} from '@wandb/weave/components/Icon';
import React, {useCallback, useContext, useState} from 'react';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Image} from 'semantic-ui-react';

import * as ViewActions from '../../state/views/actions';
import {useViewRefAction} from '../../state/views/hooks';
import {PermissionDropdown} from './PermissionDropdown';
import {SharedWithIndividualsSection} from './SharedWithIndividualsSection';
import * as S from './ShareReportTrigger.styles';
import {ShareReportTriggerContext} from './ShareReportTriggerContextProvider';

const LINK_PERMISSION_EDIT_VALUE = 'edit';
const LINK_PERMISSION_VIEW_VALUE = 'view';

const PERMISSION_OPTIONS: Array<{name: string; value: string; icon: IconName}> =
  [
    {
      name: 'Can edit',
      value: LINK_PERMISSION_EDIT_VALUE,
      icon: 'pencil-edit',
    },
    {
      name: 'Can view',
      value: LINK_PERMISSION_VIEW_VALUE,
      icon: 'show-visible',
    },
  ];

function getLinkPermissionFromViewLocked(locked: boolean) {
  const linkPermissionVal = locked
    ? LINK_PERMISSION_VIEW_VALUE
    : LINK_PERMISSION_EDIT_VALUE;
  return (
    PERMISSION_OPTIONS.find(o => o.value === linkPermissionVal) ??
    PERMISSION_OPTIONS[0]
  );
}

export const ShareWithSection = ({isTeam}: {isTeam: boolean}) => {
  const {
    canChangeSharingSettings,
    isPrivateWriteAccess,
    userIsMemberOfTeam,
    viewRef,
    view,
    viewer,
  } = useContext(ShareReportTriggerContext);

  const [openDropdown, setOpenDropdown] = useState<string>('');
  const setLocked = useViewRefAction(viewRef, ViewActions.setLocked);
  const [linkPermission, setLinkPermission] = useState(
    getLinkPermissionFromViewLocked(view.locked)
  );
  const setLinkPermissionByValue = useCallback(
    (v: string | number) => {
      if (!canChangeSharingSettings) {
        return;
      }
      const lp = PERMISSION_OPTIONS.find(o => o.value === v);
      if (lp != null) {
        setLinkPermission(lp);
        setLocked(v !== LINK_PERMISSION_EDIT_VALUE);
      }
    },
    [setLocked, canChangeSharingSettings]
  );

  if (isTeam && (!isPrivateWriteAccess || !userIsMemberOfTeam)) {
    return null;
  }

  return (
    <S.ControlsWrapper>
      {isTeam ? (
        <S.RowWrapper>
          <S.IconTextWrapper>
            <S.GrayCircleWBIcon name="user-profile-personal" />
            {`${view.project?.entityName} (team)`}
          </S.IconTextWrapper>
          <S.ShareOptionsWrapper>
            <PermissionDropdown
              id="input-dropdown"
              selectedValue={linkPermission.value}
              onItemClick={(val: string) => setLinkPermissionByValue(val)}
              options={PERMISSION_OPTIONS}
              isOpen={openDropdown === 'team'}
              setIsOpen={setOpenDropdown}
              Trigger={
                // tooltip to show that only team admin can change member permissions if viewer is not owner/admin
                <S.Tooltip
                  on="hover"
                  inverted
                  position="top center"
                  size="mini"
                  content={
                    canChangeSharingSettings
                      ? ''
                      : 'Only team admin or author can change member permissions'
                  }
                  hidden={canChangeSharingSettings}
                  trigger={
                    <Button variant="ghost">
                      <S.DropdownTrigger
                        onClick={() =>
                          setOpenDropdown(openDropdown === 'team' ? '' : 'team')
                        }
                        disabled={!canChangeSharingSettings}>
                        {linkPermission.name.toLowerCase()}
                        <Icon
                          name={
                            openDropdown === 'team'
                              ? 'chevron-up'
                              : 'chevron-down'
                          }
                        />
                      </S.DropdownTrigger>
                    </Button>
                  }
                />
              }
              onClose={() => setOpenDropdown('')}
            />
          </S.ShareOptionsWrapper>
        </S.RowWrapper>
      ) : (
        <S.RowWrapper>
          <S.IconTextWrapper>
            <Image src={viewer?.photoUrl} avatar />
            &nbsp;{`${viewer?.name || viewer?.username} (you)`}
          </S.IconTextWrapper>
          <S.SimpleSmallTextWrapper>owner</S.SimpleSmallTextWrapper>
        </S.RowWrapper>
      )}
      {canChangeSharingSettings && (
        <SharedWithIndividualsSection
          openDropdown={openDropdown}
          setOpenDropdown={setOpenDropdown}
        />
      )}
    </S.ControlsWrapper>
  );
};
